/**
 * Onde a bolinha da Vera fica, de que tamanho, e se ela aparece.
 *
 * ## Por que não é o hook do Lucas
 *
 * Metade daquele arquivo é mecânica (posição, escondido, ler e gravar com
 * try/catch) e metade é política do Lucas: a pulsada de descoberta, com
 * carimbo de tempo e descanso de 24 horas. A Vera não quer a pulsada, porque
 * as oito pessoas que a abrem já sabem que ela existe, e quer uma coisa que o
 * Lucas não tem, que é guardar o TAMANHO. Parametrizar o prefixo mexeria num
 * arquivo que atende cliente, e a regra é que a Vera importa do Lucas e nunca
 * o altera.
 *
 * O que foi emprestado é a mecânica pura: `useDraggable` e `clampPosition`,
 * importados sem tocar em nada.
 *
 * ## Por que localStorage e não banco
 *
 * Posição e tamanho são preferência de APARELHO, não de pessoa. A mesma pessoa
 * num monitor ultrawide e num notebook quer coisas diferentes, e sincronizar
 * pelo servidor produziria o lugar errado em metade das telas.
 *
 * ## Por que uma loja externa, e não useState
 *
 * A preferência é lida pela bolinha e escrita pelo painel ao redimensionar. Com
 * estado local por instância, uma não veria a mudança da outra até remontar.
 */

import { useCallback, useSyncExternalStore } from "react";

const PREFIXO = "tw:veraWidget";

/** Canto inferior direito, ACIMA de onde a bolinha do Lucas nasce. */
export const POSICAO_PADRAO = { right: 24, bottom: 92 };

/** O painel começa confortável para ler uma investigação, e cresce daí. */
export const TAMANHO_PADRAO = { width: 420, height: 620 };
export const TAMANHO_MINIMO = { width: 320, height: 360 };

const chaveDe = (userId) => `${PREFIXO}:${userId || "anon"}`;

function lerDoDisco(userId) {
  try {
    const bruto = localStorage.getItem(chaveDe(userId));
    if (!bruto) return null;
    const lido = JSON.parse(bruto);
    return lido && typeof lido === "object" ? lido : null;
  } catch {
    // Janela anônima, armazenamento bloqueado ou JSON corrompido: volta ao
    // padrão em vez de derrubar a tela inteira.
    return null;
  }
}

function gravarNoDisco(userId, valor) {
  try {
    localStorage.setItem(chaveDe(userId), JSON.stringify(valor));
  } catch {
    // Não poder lembrar não é motivo para impedir o arrasto.
  }
}

/**
 * Número utilizável, ou o padrão.
 *
 * `Math.max("grande", 320)` devolve `NaN`, e um `NaN` chegando a um
 * `style.width` abre o painel sem largura nenhuma. O disco é de fora: uma
 * versão anterior do widget, um dedo no DevTools, um JSON meio gravado.
 */
function medida(valor, padrao, minimo) {
  const n = Number(valor);
  if (!Number.isFinite(n) || n <= 0) return padrao;
  return Math.max(n, minimo);
}

function posicaoValida(bruto) {
  const right = Number(bruto?.right);
  const bottom = Number(bruto?.bottom);
  if (!Number.isFinite(right) || !Number.isFinite(bottom)) return POSICAO_PADRAO;
  return { right, bottom };
}

function normalizar(bruto) {
  return {
    position: posicaoValida(bruto?.position),
    size: {
      width: medida(bruto?.size?.width, TAMANHO_PADRAO.width, TAMANHO_MINIMO.width),
      height: medida(bruto?.size?.height, TAMANHO_PADRAO.height, TAMANHO_MINIMO.height),
    },
    hidden: bruto?.hidden === true,
  };
}

let estado = null;
let donoDoEstado = null;
const ouvintes = new Set();

function garantirEstado(userId) {
  if (estado && donoDoEstado === userId) return estado;
  donoDoEstado = userId;
  estado = normalizar(lerDoDisco(userId));
  return estado;
}

function publicar(userId, proximo) {
  estado = proximo;
  donoDoEstado = userId;
  gravarNoDisco(userId, proximo);
  for (const ouvinte of ouvintes) ouvinte();
}

function assinar(ouvinte) {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

/**
 * Se a pessoa redimensionou o painel.
 *
 * Compara com o tamanho GUARDADO, nunca com o que foi desenhado: a altura é
 * cortada pelo espaço livre na tela, então num notebook ela quase nunca bate
 * com o padrão e o botão de voltar ficaria aceso para sempre.
 */
export function tamanhoFoiAlterado(size) {
  return (
    size?.width !== TAMANHO_PADRAO.width || size?.height !== TAMANHO_PADRAO.height
  );
}

/** Se a pessoa arrastou o atalho para fora do canto onde ele nasce. */
export function foiMovida(position) {
  return (
    position?.right !== POSICAO_PADRAO.right ||
    position?.bottom !== POSICAO_PADRAO.bottom
  );
}

/** Só para os testes: esquece o que ficou de um cenário para o outro. */
export function _resetVeraWidgetPrefs() {
  estado = null;
  donoDoEstado = null;
}

export default function useVeraWidgetPrefs(userId) {
  const prefs = useSyncExternalStore(
    assinar,
    () => garantirEstado(userId),
    () => normalizar(null),
  );

  const moverPara = useCallback(
    (position) => publicar(userId, { ...garantirEstado(userId), position }),
    [userId],
  );

  const redimensionarPara = useCallback(
    (size) =>
      publicar(userId, {
        ...garantirEstado(userId),
        size: {
          width: medida(size?.width, TAMANHO_PADRAO.width, TAMANHO_MINIMO.width),
          height: medida(size?.height, TAMANHO_PADRAO.height, TAMANHO_MINIMO.height),
        },
      }),
    [userId],
  );

  /**
   * Ligar e desligar o atalho.
   *
   * Esconder, sozinho, era porta sem maçaneta do lado de dentro: o X no painel
   * tirava a Vera da tela e não sobrava de onde trazê-la de volta. Quem some
   * com um atalho procura onde se liga e desliga coisa, que é a tela de
   * preferências, e não a sidebar.
   */
  const definirEscondido = useCallback(
    (hidden) => publicar(userId, { ...garantirEstado(userId), hidden }),
    [userId],
  );

  const esconder = useCallback(() => definirEscondido(true), [definirEscondido]);

  const voltarAoPadrao = useCallback(
    () => publicar(userId, { ...garantirEstado(userId), position: POSICAO_PADRAO }),
    [userId],
  );

  return {
    prefs,
    moverPara,
    redimensionarPara,
    esconder,
    definirEscondido,
    voltarAoPadrao,
  };
}
